import { useEffect, useState } from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import DataTable from 'examples/Tables/DataTable';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ENDPOINT } from 'constants';
import MDButton from 'components/MDButton';
import {
  Card,
  CircularProgress,
  Menu,
  MenuItem,
  Grid,
  Divider,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  TextField,
  Button,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { Loading, Notify } from 'notiflix';
import { Controller, useForm } from 'react-hook-form';
import DayPicker from '../DayPicker';

const columns = [
  { Header: '#', accessor: 'sr', width: '5%', align: 'left' },
  { Header: 'Client Name', accessor: 'name', width: '30%', align: 'left' },
  { Header: 'Email', accessor: 'email', align: 'left' },
  { Header: 'Reminder Override', accessor: 'override', align: 'center' },
  { Header: 'Status', accessor: 'status', align: 'center' },
  { Header: 'Action', accessor: 'action', align: 'center' },
];

const DEFAULT_FORM = {
  overrideGlobal: false,
  disableReminders: false,
  daysBeforeDue: [],
  onDueDate: false,
  daysAfterDue: [],
  autoCancelAfterDays: 0,
  cartReminderDays: [],
  channels: ['email'],
};

const ManageUserReminderSettings = () => {
  const [users, setUsers] = useState([]);
  const [userSettingsMap, setUserSettingsMap] = useState({}); // userId → has override
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [menu, setMenu] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm({ defaultValues: DEFAULT_FORM });
  const disableReminders = watch('disableReminders');
  const overrideGlobal = watch('overrideGlobal');

  // ── Fetch all users ──────────────────────────────────────────────────────
  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${ENDPOINT}api/v1/user/admin/users`, {
        headers: { Authorization: `Bearer ${user?.accessToken}` },
      });
      setUsers(data.data.users);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // ── Menu ─────────────────────────────────────────────────────────────────
  const openMenu = (e, row) => { setSelectedUser(row); setMenu(e.currentTarget); };
  const closeMenu = () => setMenu(null);

  // ── Open reminder dialog for a user ──────────────────────────────────────
  const handleOpenReminderDialog = async () => {
    closeMenu();
    Loading.circle('Loading reminder settings...');
    try {
      const { data } = await axios.get(
        `${ENDPOINT}api/v1/admin/reminder/user-settings/${selectedUser.id}`,
        { headers: { Authorization: `Bearer ${user?.accessToken}` } },
      );
      const s = data.data;
      reset({
        ...DEFAULT_FORM,
        ...s,
        channels: s.channels ?? ['email'],
        daysBeforeDue: s.daysBeforeDue ?? [],
        daysAfterDue: s.daysAfterDue ?? [],
        cartReminderDays: s.cartReminderDays ?? [],
      });
      setHasExistingSettings(true);
    } catch (err) {
      if (err.response?.status === 404) {
        reset(DEFAULT_FORM);
        setHasExistingSettings(false);
      } else {
        Notify.failure('Failed to fetch user reminder settings');
        return;
      }
    } finally {
      Loading.remove();
      setDialogOpen(true);
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const onSubmit = async (formData) => {
    try {
      Loading.circle('Saving...');
      // Use PUT which upserts (creates if not exists, updates if it does)
      await axios.put(
        `${ENDPOINT}api/v1/admin/reminder/user-settings/${selectedUser.id}`,
        formData,
        { headers: { Authorization: `Bearer ${user?.accessToken}` } },
      );
      Notify.success('Reminder settings saved!');
      setDialogOpen(false);
      // Refresh override indicator
      setUserSettingsMap((prev) => ({ ...prev, [selectedUser.id]: true }));
    } catch (err) {
      console.error(err);
      Notify.failure('Failed to save reminder settings');
    } finally {
      Loading.remove();
    }
  };

  // ── Reset user to global ──────────────────────────────────────────────────
  const handleResetToGlobal = async () => {
    if (!window.confirm(`Reset ${selectedUser?.firstName}'s reminder settings to global defaults?`)) return;
    try {
      Loading.circle('Resetting...');
      await axios.post(
        `${ENDPOINT}api/v1/admin/reminder/user-settings/${selectedUser.id}/reset`,
        {},
        { headers: { Authorization: `Bearer ${user?.accessToken}` } },
      );
      Notify.success('Reset to global settings');
      setDialogOpen(false);
      setUserSettingsMap((prev) => ({ ...prev, [selectedUser.id]: false }));
    } catch (err) {
      console.error(err);
      Notify.failure('Failed to reset settings');
    } finally {
      Loading.remove();
    }
  };

  // ── Table rows ────────────────────────────────────────────────────────────
  const filteredUsers = users.filter((u) =>
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getRow = (row, idx) => ({
    sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
    name: (
      <MDTypography variant="button" fontWeight="medium" color="secondary">
        {row.firstName} {row.lastName}
      </MDTypography>
    ),
    email: <MDTypography variant="caption">{row.email}</MDTypography>,
    override: userSettingsMap[row.id] ? (
      <Chip label="Custom" color="warning" size="small" />
    ) : (
      <Chip label="Global" color="default" size="small" />
    ),
    status: (
      <MDBadge
        badgeContent={row.status === 'active' ? 'active' : 'inactive'}
        color={row.status === 'active' ? 'success' : 'secondary'}
        variant="gradient"
        size="sm"
      />
    ),
    action: (
      <Icon sx={{ cursor: 'pointer' }} fontSize="small" onClick={(e) => openMenu(e, row)}>
        more_vert
      </Icon>
    ),
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={4} mb={3}>
        <MDButton
          variant="text"
          color="dark"
          onClick={() => navigate('/remindersettings')}
          sx={{ mb: 2 }}
        >
          <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
          Back to Reminder Settings
        </MDButton>

        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}
              >
                <MDBox>
                  <MDTypography variant="h6" color="white">
                    Per-user reminder overrides
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                    Search a user to view or change their reminder settings — everyone else keeps the global defaults.
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox my={3} mx={2}>
                <TextField
                  fullWidth
                  label="Search by name"
                  placeholder="Start typing a client's name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </MDBox>

              {loading ? (
                <Grid container justifyContent="center" mb={6}>
                  <CircularProgress size="3rem" color="inherit" />
                </Grid>
              ) : (
                <MDBox pt={1}>
                  <DataTable
                    table={{ columns, rows: filteredUsers.map(getRow) }}
                    isSorted={false}
                    entriesPerPage={{ defaultValue: 25, entries: [10, 25, 50, 100] }}
                    showTotalEntries
                    noEndBorder
                    hoverEffect
                    canSearch={false}
                  />
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* ── Context menu ── */}
      <Menu anchorEl={menu} open={Boolean(menu)} onClose={closeMenu}>
        <MenuItem onClick={handleOpenReminderDialog}>Manage Reminder Settings</MenuItem>
      </Menu>

      {/* ── Settings dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reminder Settings — {selectedUser?.firstName} {selectedUser?.lastName}
          {hasExistingSettings && (
            <Chip label="Custom override active" color="warning" size="small" sx={{ ml: 1 }} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          <form id="reminder-form" onSubmit={handleSubmit(onSubmit)}>
            <MDBox mb={2} p={1.5} sx={{ bgcolor: 'error.main', opacity: disableReminders ? 1 : 0.08, borderRadius: 1, transition: 'opacity 0.15s' }}>
              <Controller
                name="disableReminders"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} color="error" onChange={(e) => field.onChange(e.target.checked)} />}
                    label={
                      <MDTypography variant="button" fontWeight="medium" color={disableReminders ? 'white' : 'text'}>
                        Disable all reminders for this user
                      </MDTypography>
                    }
                  />
                )}
              />
              <MDTypography variant="caption" color={disableReminders ? 'white' : 'secondary'} display="block" sx={{ pl: 4.5 }}>
                Stops every reminder job for this user immediately — works independently of the override switch below.
              </MDTypography>
            </MDBox>

            <Divider sx={{ my: 2 }} />

            <Controller
              name="overrideGlobal"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Override global frequency &amp; channel settings for this user"
                />
              )}
            />
            <MDTypography variant="caption" color="secondary" display="block" sx={{ pl: 4.5 }} mb={1}>
              {overrideGlobal
                ? 'The fields below replace the global defaults for this user.'
                : 'Off — the fields below are ignored; global defaults apply.'}
            </MDTypography>

            <MDBox sx={{ opacity: overrideGlobal ? 1 : 0.45, pointerEvents: overrideGlobal ? 'auto' : 'none' }}>
              <Divider sx={{ my: 2 }} />
              <Controller
                name="onDueDate"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Send reminder on due date"
                    sx={{ mb: 2, display: 'block' }}
                  />
                )}
              />
              <Controller
                name="channels"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value?.includes('sms')}
                        onChange={(e) =>
                          field.onChange(e.target.checked ? ['email', 'sms'] : ['email'])
                        }
                      />
                    }
                    label="Also send SMS (email always included)"
                    sx={{ mb: 1, display: 'block' }}
                  />
                )}
              />
              <Divider sx={{ my: 2 }} />
              <DayPicker control={control} name="daysBeforeDue" label="Days before due" />
              <DayPicker control={control} name="daysAfterDue" label="Days after due" />
              <DayPicker control={control} name="cartReminderDays" label="Cart reminder days" />
              <Controller
                name="autoCancelAfterDays"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth label="Auto-cancel unpaid orders after (days)" type="number"
                    inputProps={{ min: 0 }} {...field}
                    helperText="0 = disabled for this user"
                  />
                )}
              />
            </MDBox>
          </form>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          {hasExistingSettings && (
            <MDButton variant="outlined" color="error" size="small" onClick={handleResetToGlobal}>
              Reset to Global
            </MDButton>
          )}
          <MDBox display="flex" gap={1} ml="auto">
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <MDButton form="reminder-form" type="submit" variant="gradient" color="info">
              Save
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default ManageUserReminderSettings;
